import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { emailArchiveService } from '../../services/api';
import { fmtDateTime } from '../../utils/formatters';
import { TONE_BADGE } from '../../utils/statusColors';
import { campo, mensagemDoErroDeCarga, opcaoAtualDispensada, opcaoAtualForaDaLista } from '../../design';
import CascaDeGaveta from '../shared/CascaDeGaveta';
import {
  agruparEspelho, anualidadeProposta, contratoProposto, EmailDoEspelho, GRUPO_DO_ESPELHO, seloDoMotivo,
} from '../../utils/filaDoEspelho';

/**
 * Espelho da tratativa: o cartão "E-mails para confirmar" no topo de Reajustes e a fila numa gaveta (L1), agrupada
 * pela força do palpite (G1), com a anualidade viva já escolhida quando ela é a única do contrato (A1). Prévia aprovada
 * pelo Heitor em 31/10/2026: https://claude.ai/artifact/21RrjLfSGmrCZmEeTm5eck
 *
 * ⛔ "Não é tratativa" NÃO apaga: o e-mail segue no arquivo. Não existe "excluir" nesta tela.
 * ⚠️ Zero tem dois sentidos, e a tela diz qual: o arquivo nunca recebeu e-mail (o espelho ainda não está ligado), ou
 * recebeu e não sobrou nada para confirmar.
 */

const fantasma = 'px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap border border-central-borda bg-central-cartao text-central-texto hover:bg-central-elevado disabled:opacity-40';

interface Contrato { id: number; contractNumber?: string | null; client?: { name?: string; tradeName?: string; companyName?: string } }
interface Anualidade { id: number; contractId: number | string; sequence?: number; status: string }

const nomeDoCliente = (c?: Contrato) => c?.client?.name?.trim() || c?.client?.tradeName || c?.client?.companyName || `contrato ${c?.id}`;

export default function FilaDoEspelho({ contratos, anualidades, onConfirmado }: {
  /** Os contratos que a tela de Reajustes já leu (para escolher ou trocar o contrato). */
  contratos: Contrato[];
  anualidades: Anualidade[];
  /** Um e-mail virou registro: a lista de Reajustes recarrega, e o marcador do último contato muda. */
  onConfirmado: () => void;
}) {
  const [sugestoes, setSugestoes] = useState<EmailDoEspelho[]>([]);
  const [semContrato, setSemContrato] = useState<EmailDoEspelho[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [arquivoVazio, setArquivoVazio] = useState<boolean | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aberta, setAberta] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const [s, sc, arq] = await Promise.all([
        emailArchiveService.sugestoes(200, 0),
        emailArchiveService.listar({ semCliente: true, limit: 200 }),
        emailArchiveService.listar({ limit: 1 }),
      ]);
      setSugestoes(Array.isArray(s?.data) ? s.data : []);
      setSemContrato(Array.isArray(sc?.data) ? sc.data.filter((x: any) => !x.suggestionDismissedAt) : []);
      setTotal((Number(s?.total) || 0) + (Array.isArray(sc?.data) ? sc.data.filter((x: any) => !x.suggestionDismissedAt).length : 0));
      setArquivoVazio(Number(arq?.total) === 0);
      setErro(null);
    } catch (e) {
      setErro(mensagemDoErroDeCarga(e));
    }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const grupos = useMemo(() => agruparEspelho(sugestoes, semContrato), [sugestoes, semContrato]);
  const ambiguos = sugestoes.filter((x) => x.motivoDoPalpite === 'contrato_ambiguo').length;
  const n = total ?? 0;

  return (
    <>
      <button
        type='button'
        onClick={() => setAberta(true)}
        disabled={total === null && !erro}
        className={`rounded-xl border border-central-borda bg-central-cartao p-4 text-left min-w-[14rem] transition-colors hover:bg-central-elevado ${n > 0 ? 'border-l-[3px] border-l-amber-500' : ''} ${n === 0 && !erro ? 'opacity-70' : ''}`}
        data-cartao-espelho
        aria-label={`E-mails para confirmar: ${erro ? 'não carregou' : total ?? '…'}`}
      >
        <p className='text-sm text-slate-400'>E-mails para confirmar</p>
        <p className='text-2xl font-bold text-white mt-1 tabular-nums'>{erro ? '—' : total ?? '…'}</p>
        <p className='text-xs text-slate-500 mt-1' data-sub-espelho>
          {erro
            ? 'não carregou'
            : arquivoVazio
              ? 'o espelho ainda não recebeu nenhum e-mail'
              : n === 0
                ? 'nada para confirmar'
                : ambiguos > 0 ? `${ambiguos} com contrato ambíguo` : 'o HUB palpitou o contrato'}
        </p>
      </button>

      {aberta && (
        <CascaDeGaveta
          titulo='E-mails para confirmar'
          subtitulo='O e-mail com certeza (número do ofício) já entrou sozinho. Estes são palpites.'
          onClose={() => setAberta(false)}
          largura='painel'
          rotulo='E-mails para confirmar'
          temEdicaoNaoSalva={false}
        >
          <div className='space-y-4'>
            {erro && (
              <p role='alert' className={`px-3 py-2 rounded-lg text-sm ${TONE_BADGE.danger}`}>
                Não consegui ler a fila ({erro}). <button type='button' className='underline' onClick={carregar}>Tentar de novo</button>
              </p>
            )}
            {!erro && grupos.length === 0 && (
              <p className='px-3 py-6 text-center text-sm text-central-texto-fraco border border-dashed border-central-borda rounded-lg' data-espelho-vazio>
                {arquivoVazio
                  ? 'O espelho ainda não recebeu nenhum e-mail. Ele começa a trabalhar quando o endereço estiver configurado e for usado em cópia.'
                  : 'Nada para confirmar: todo e-mail que chegou já virou registro ou foi marcado como não sendo tratativa.'}
              </p>
            )}
            {grupos.map((g) => (
              <section key={g.grupo} aria-label={GRUPO_DO_ESPELHO[g.grupo].titulo} data-grupo-espelho={g.grupo} className='rounded-xl border border-central-borda overflow-hidden'>
                <header className={`px-4 py-2.5 bg-central-elevado border-b border-central-borda ${g.grupo === 'dominio' || g.grupo === 'semContrato' ? '' : 'border-l-[3px] border-l-amber-500'}`}>
                  <h3 className='text-sm font-semibold text-central-texto'>{GRUPO_DO_ESPELHO[g.grupo].titulo} <span className='text-central-texto-fraco font-normal'>· {g.emails.length}</span></h3>
                  <p className='text-xs text-central-texto-fraco'>{GRUPO_DO_ESPELHO[g.grupo].sub}</p>
                </header>
                <ul>
                  {g.emails.map((e) => (
                    <ItemDoEspelho
                      key={e.id}
                      email={e}
                      contratos={contratos}
                      anualidades={anualidades}
                      onFeito={(confirmado) => { carregar(); if (confirmado) onConfirmado(); }}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </CascaDeGaveta>
      )}
    </>
  );
}

function ItemDoEspelho({ email: e, contratos, anualidades, onFeito }: {
  email: EmailDoEspelho;
  contratos: Contrato[];
  anualidades: Anualidade[];
  onFeito: (confirmado: boolean) => void;
}) {
  const [contrato, setContrato] = useState<number | null>(() => contratoProposto(e));
  const [anualidade, setAnualidade] = useState<number | null>(() => anualidadeProposta(contratoProposto(e), anualidades));
  const [lendo, setLendo] = useState(false);
  const [corpo, setCorpo] = useState<any | null>(null);
  const [erroDoCorpo, setErroDoCorpo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const candidatos = e.contratosCandidatos || [];
  const ambiguo = e.motivoDoPalpite === 'contrato_ambiguo' && candidatos.length > 1;
  const selo = seloDoMotivo(e);
  const doContrato = anualidades
    .filter((a) => Number(a.contractId) === Number(contrato))
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  const outros = contratos
    .filter((c) => !candidatos.some((k) => k.id === c.id))
    .sort((a, b) => nomeDoCliente(a).localeCompare(nomeDoCliente(b)));
  const rotuloDoContrato = (id: number) => {
    const k = candidatos.find((x) => x.id === id);
    if (k) return `${k.clientName} (${k.id}) · ${k.contractNumber}`;
    const c = contratos.find((x) => x.id === id);
    return c ? `${nomeDoCliente(c)} (${c.id})${c.contractNumber ? ` · ${c.contractNumber}` : ''}` : `contrato ${id}`;
  };

  const trocarContrato = (id: number | null) => {
    setContrato(id);
    setAnualidade(anualidadeProposta(id, anualidades));
  };

  const ler = async () => {
    if (lendo) { setLendo(false); return; }
    setLendo(true);
    if (corpo) return;
    try { setCorpo(await emailArchiveService.detalhe(e.id)); setErroDoCorpo(null); } catch (x) { setErroDoCorpo(mensagemDoErroDeCarga(x)); }
  };

  const baixar = async (key?: string) => {
    if (!key) return;
    try {
      const r = await emailArchiveService.anexo(e.id, key);
      if (r?.url) window.open(r.url, '_blank', 'noopener');
      else setErro('O anexo não está mais no arquivo.');
    } catch (x) { setErro(`Não abri o anexo: ${mensagemDoErroDeCarga(x)}`); }
  };

  const confirmar = async () => {
    if (!contrato || ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      const r = await emailArchiveService.confirmar(e.id, { contractId: contrato, adjustmentId: anualidade });
      if (r?.ok) { onFeito(true); return; }
      setErro(r?.erro || 'O servidor não registrou e não disse o motivo.');
    } catch (x) { setErro(`Não confirmou: ${mensagemDoErroDeCarga(x)}`); }
    setOcupado(false);
  };

  const descartar = async () => {
    if (ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      // `descartada: false` = já estava descartado (outro clique, outra aba): sucesso do mesmo jeito.
      await emailArchiveService.descartar(e.id);
      onFeito(false);
      return;
    } catch (x) { setErro(`Não marquei: ${mensagemDoErroDeCarga(x)}`); }
    setOcupado(false);
  };

  const anexos = e.attachments || [];
  return (
    <li className='px-4 py-3 border-b last:border-b-0 border-central-borda space-y-2' data-email={e.id}>
      <div className='flex flex-wrap items-start justify-between gap-2'>
        <div className='min-w-0'>
          <p className='text-sm font-medium text-central-texto break-words'>
            {e.subject || '(sem assunto)'}
            {selo && <span className={`ml-2 px-2 py-0.5 rounded-full text-[11px] font-semibold align-middle ${e.motivoDoPalpite === 'contrato_ambiguo' ? TONE_BADGE.danger : TONE_BADGE.neutral}`} data-motivo-do-palpite>{selo}</span>}
          </p>
          <p className='text-xs text-central-texto-fraco'>
            {e.direction === 'enviado' ? 'enviado' : 'recebido'} · {e.fromName ? `${e.fromName} <${e.fromEmail}>` : e.fromEmail}
            {e.emailDate ? ` · ${fmtDateTime(e.emailDate)}` : ''}
            {anexos.length ? ` · ${anexos.length} ${anexos.length === 1 ? 'anexo' : 'anexos'}` : ''}
          </p>
        </div>
        <button type='button' className={fantasma} onClick={ler} aria-expanded={lendo}>{lendo ? 'Fechar o e-mail' : 'Ler o e-mail'}</button>
      </div>

      {ambiguo && (
        <p className='border-l-[3px] border-l-amber-500 bg-amber-500/10 rounded-md px-3 py-1.5 text-xs text-central-texto' data-ambiguo>
          O número citado é de {candidatos.length} contratos. Escolha de qual é a tratativa.
        </p>
      )}

      {lendo && (
        <div className='rounded-lg border border-central-borda bg-central-fundo p-2 space-y-2' data-corpo-do-email>
          {erroDoCorpo ? (
            <p role='alert' className={`px-3 py-2 rounded-lg text-sm ${TONE_BADGE.danger}`}>Não consegui ler o e-mail: {erroDoCorpo}</p>
          ) : !corpo ? (
            <p className='text-xs text-central-texto-fraco'>Lendo o e-mail…</p>
          ) : corpo.bodyHtml ? (
            <iframe title={`E-mail ${e.subject || e.id}`} sandbox='' srcDoc={corpo.bodyHtml} className='w-full h-[360px] rounded bg-white' />
          ) : (
            <pre className='whitespace-pre-wrap text-xs text-central-texto max-h-[360px] overflow-auto'>{corpo.bodyText || '(e-mail sem texto)'}</pre>
          )}
          {anexos.length > 0 && (
            <div className='flex flex-wrap gap-1.5'>
              {anexos.map((a, i) => (
                <button key={`${a.s3Key || a.filename}-${i}`} type='button' className={fantasma} disabled={!a.s3Key} onClick={() => baixar(a.s3Key)}>📎 {a.filename}</button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className='flex flex-wrap items-center gap-2'>
        <label className='text-xs text-central-texto-fraco inline-flex items-center gap-1.5'>Contrato
          <select value={contrato ?? ''} onChange={(x) => trocarContrato(x.target.value ? Number(x.target.value) : null)} className={`max-w-[18rem] px-2 py-1 rounded-lg text-xs ${campo}`} aria-label={`Contrato do e-mail ${e.id}`}>
            <option value=''>escolha o contrato…</option>
            {opcaoAtualForaDaLista([...candidatos, ...outros].map((c) => ({ id: c.id })), contrato, contrato ? rotuloDoContrato(contrato) : '', false)}
            {candidatos.length > 0 && (
              <optgroup label='Citados no texto'>
                {candidatos.map((k) => <option key={k.id} value={k.id}>{rotuloDoContrato(k.id)}</option>)}
              </optgroup>
            )}
            <optgroup label={candidatos.length ? 'Outros contratos' : 'Contratos'}>
              {outros.map((c) => <option key={c.id} value={c.id}>{rotuloDoContrato(c.id)}</option>)}
            </optgroup>
          </select>
        </label>
        <label className='text-xs text-central-texto-fraco inline-flex items-center gap-1.5'>Anualidade
          <select value={anualidade ?? ''} onChange={(x) => setAnualidade(x.target.value ? Number(x.target.value) : null)} disabled={!contrato} className={`px-2 py-1 rounded-lg text-xs ${campo}`} aria-label={`Anualidade do e-mail ${e.id}`}>
            {opcaoAtualDispensada('as anualidades saem da lista já lida pela tela, do contrato escolhido')}
            <option value=''>nenhuma: linha do tempo do contrato</option>
            {doContrato.map((a) => <option key={a.id} value={a.id}>{a.sequence ? `${a.sequence}ª` : `#${a.id}`}, {a.status}</option>)}
          </select>
        </label>
        <span className='flex-1' />
        <button type='button' className={fantasma} disabled={!contrato || ocupado} onClick={confirmar}>{ocupado ? 'Registrando…' : 'Confirmar'}</button>
        <button type='button' className={fantasma} disabled={ocupado} onClick={descartar} title='O e-mail continua no arquivo, com o corpo inteiro. Só sai desta fila.'>Não é tratativa</button>
      </div>
      {erro && <p role='alert' className={`px-3 py-2 rounded-lg text-sm ${TONE_BADGE.danger}`}>{erro}</p>}
    </li>
  );
}
